import _ from 'es-toolkit/compat';
import { DateTime, Duration, Settings } from 'luxon';
import { DateFormatPatterns, Locale } from '@clearverify/constants';
const PatchedSettings = Settings;
PatchedSettings.twoDigitCutoffYear = new Date().getFullYear();
PatchedSettings.defaultLocale = Locale.US;
export function formatDate(date, toFormatPattern, fromFormatPattern) {
    if (_.isNil(date))
        return null;
    if (_.isNil(fromFormatPattern)) {
        const formattedDate = DateTime.fromISO(date);
        return formattedDate.isValid ? formattedDate.toFormat(toFormatPattern) : null;
    }
    const formattedDate = DateTime.fromFormat(date, fromFormatPattern);
    return formattedDate.isValid ? formattedDate.toFormat(toFormatPattern) : null;
}
export function formatDateToISO(date, fromFormatPattern, useTwoDigitsCutOfYear = true) {
    if (_.isNil(date))
        return null;
    if (!useTwoDigitsCutOfYear) {
        PatchedSettings.twoDigitCutoffYear = 99;
    }
    const formatedDate = fromFormatPattern
        ? DateTime.fromFormat(date, fromFormatPattern)
        : DateTime.fromISO(date);
    PatchedSettings.twoDigitCutoffYear = new Date().getFullYear();
    return formatedDate.isValid ? formatedDate.toISODate() : null;
}
export function getNowDate() {
    return DateTime.local().toISODate();
}
export function getNowDateTime() {
    return DateTime.local().toISO();
}
export function getNowWeekdayAndDate() {
    return `${DateTime.local().weekdayLong}, ${DateTime.local().monthLong} ${DateTime.local().day}`;
}
export function getNowDateWithWeekday() {
    return DateTime.local().toLocaleString(DateTime.DATE_HUGE);
}
export function toDateTimeFromISO(dateISO) {
    if (_.isNil(dateISO)) {
        return null;
    }
    return DateTime.fromISO(dateISO);
}
export function getEpochFromDate(date) {
    if (_.isNil(date))
        return null;
    const dateTime = _.isDate(date) ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
    return dateTime.toMillis();
}
export function isValidDate(date) {
    if (_.isNil(date)) {
        return false;
    }
    return DateTime.fromISO(date).isValid;
}
export function isValidFormatDate(date, formatPattern) {
    if (_.isNil(date)) {
        return false;
    }
    return DateTime.fromFormat(date, formatPattern).isValid;
}
// Luxon's fromFormat is working a little bit strange
// e.g. When formatPattern='MM.dd.yy' it will interpret it as /^\d{2}\.\d{2}\.\d{2,4}$/
// Which is not a desired behaviour as it makes date='01.01.199' a valid date
// This fn additionally iterates over format tokens and compares lengths with actual values
export function isValidFormatDateStrict(date, formatPattern) {
    if (_.isNil(date) || !DateTime.fromFormat(date, formatPattern).isValid) {
        return false;
    }
    const explain = DateTime.fromFormatExplain(date, formatPattern);
    const { tokens, rawMatches } = explain;
    return rawMatches && tokens.every((token, i) => token.val.length === rawMatches[i + 1]?.length);
}
export function trimTime(date) {
    if (_.isNil(date)) {
        return null;
    }
    return DateTime.fromISO(date).toISODate();
}
export function trimDate(date) {
    if (_.isNil(date)) {
        return null;
    }
    return DateTime.fromISO(date).toISOTime();
}
export function isDateEquals(d1, d2, unit = 'day') {
    if (_.isNil(d1) && _.isNil(d2))
        return true;
    if (_.isNil(d1) || _.isNil(d2))
        return false;
    const d1ISOString = _.isDate(d1) ? d1.toISOString() : d1;
    const d2ISOString = _.isDate(d2) ? d2.toISOString() : d2;
    const dateA = DateTime.fromISO(d1ISOString).startOf(unit);
    const dateB = DateTime.fromISO(d2ISOString).startOf(unit);
    return dateA.equals(dateB);
}
export function isDateGreaterThan(d1, d2, unit = 'day') {
    if (_.isNil(d1) || _.isNil(d2)) {
        return false;
    }
    const d1ISOString = _.isDate(d1) ? d1.toISOString() : d1;
    const d2ISOString = _.isDate(d2) ? d2.toISOString() : d2;
    const dateA = DateTime.fromISO(d1ISOString).startOf(unit);
    const dateB = DateTime.fromISO(d2ISOString).startOf(unit);
    return dateA > dateB;
}
export function isDateGreaterOrEquals(date1, date2, unit = 'day') {
    if (_.isNil(date1) && _.isNil(date2))
        return true;
    if (_.isNil(date1) || _.isNil(date2))
        return false;
    const date1ISOString = _.isDate(date1) ? date1.toISOString() : date1;
    const date2ISOString = _.isDate(date2) ? date2.toISOString() : date2;
    return (DateTime.fromISO(date1ISOString).startOf(unit) >= DateTime.fromISO(date2ISOString).startOf(unit));
}
export function isDateLessOrEquals(dateA, dateB, unit = 'day') {
    if (_.isNil(dateA) && _.isNil(dateB))
        return true;
    if (_.isNil(dateA) || _.isNil(dateB))
        return false;
    return DateTime.fromISO(dateA).startOf(unit) <= DateTime.fromISO(dateB).startOf(unit);
}
export function getStartOf(date, unit = 'month') {
    return DateTime.fromISO(date).startOf(unit).toISO();
}
export function getEndOf(date, unit = 'month') {
    return DateTime.fromISO(date).endOf(unit).toISO();
}
export function getStartOfMonth(date) {
    return getStartOf(date, 'month');
}
export function getStartOfWeek(date) {
    return getStartOf(date, 'week');
}
export function getStartOfYear(date) {
    return getStartOf(date, 'year');
}
export function getEndOfWeek(date) {
    return getEndOf(date, 'week');
}
export function getEndOfMonth(date) {
    return getEndOf(date, 'month');
}
export function getEndOfYear(date) {
    return getEndOf(date, 'year');
}
export function getDaysIn(dateISO, unit) {
    if (unit === 'month') {
        return DateTime.fromISO(dateISO).daysInMonth;
    }
    return DateTime.fromISO(dateISO).daysInYear;
}
export function getMaxDateTime(dateA, dateB) {
    if (_.isNil(dateA) && _.isNil(dateB)) {
        return null;
    }
    if (_.isNil(dateA)) {
        return dateB;
    }
    if (_.isNil(dateB)) {
        return dateA;
    }
    if (isDateGreaterOrEquals(dateA, dateB, 'minute')) {
        return dateA;
    }
    return dateB;
}
export function getMinDateTime(dateA, dateB) {
    if (_.isNil(dateA) && _.isNil(dateB)) {
        return null;
    }
    if (_.isNil(dateA)) {
        return dateB;
    }
    if (_.isNil(dateB)) {
        return dateA;
    }
    if (isDateLessOrEquals(dateA, dateB, 'minute')) {
        return dateA;
    }
    return dateB;
}
export function getPastDaysFromNow(isoDate) {
    if (_.isNil(isoDate)) {
        return 0;
    }
    const rawDays = DateTime.fromISO(isoDate).diffNow('days').negate().days;
    return Math.floor(rawDays);
}
export function addDate(date, unit = 'month', count = 1) {
    if (_.isNil(date)) {
        return null;
    }
    return DateTime.fromISO(date)
        .plus({ [unit]: count })
        .toISO();
}
export function subtractDate(date, unit = 'month', count = 1) {
    if (_.isNil(date)) {
        return null;
    }
    return DateTime.fromISO(date)
        .minus({ [unit]: count })
        .toISO();
}
export const formatMonthAndYear = (month, year) => DateTime.now().set({ month, year }).toFormat('MMM yy`');
export const formatDateToShort = (date) => DateTime.fromJSDate(date).toFormat('MMM yy`');
export const getDateTimeInTimezone = (date, timezone) => {
    if (!date)
        return null;
    const dateTime = _.isString(date) ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
    return dateTime.setZone(timezone?.split(' - ')[0]);
};
export const getDatesDiff = (startDate, endDate) => startDate && endDate ? endDate.diff(startDate, ['years', 'months', 'days']) : undefined;
export const getDateFormat = (date) => {
    for (const format of Object.values(DateFormatPatterns)) {
        if (isValidFormatDateStrict(date, format)) {
            return format;
        }
    }
    return undefined;
};
export const formatDurationFromDays = (numberOfDays) => {
    const years = Math.floor(numberOfDays / 365);
    const leapYears = Math.floor(years / 4);
    const numberOfDaysFixed = numberOfDays + leapYears;
    const months = Math.floor((numberOfDaysFixed % 365) / 30);
    const days = Math.floor((numberOfDaysFixed % 365) % 30);
    // rescale() normalizes (e.g. 12 months -> 1 year) and drops any zero units,
    // so "2 years, 0 months, 4 days" renders as "2 years, 4 days"
    const duration = Duration.fromObject({ years, months, days }).rescale();
    return duration.toHuman({ maximumFractionDigits: 0 });
};
/** Parses an ISO date string to a local-midnight Date; null when missing or invalid. */
export const parseDateOrNull = (date) => {
    if (!date)
        return null;
    const dt = DateTime.fromISO(date);
    return dt.isValid ? dt.toJSDate() : null;
};
//# sourceMappingURL=date.js.map